/**
 * Payment-Link Link-Tester — HMAC-Engine (Spec 501, Phase 1).
 *
 * Core-eigene, rein clientseitige Signatur-Primitiven. HMAC laeuft ueber WebCrypto
 * (`crypto.subtle`) — kein Netzwerk, keine Dependency, kein Import aus dem
 * Devportal-Track (die Devportal-`signature-core.ts` ist nur Lese-Referenz).
 *
 * Kern-Konvention (Spec §Scope 2, teuerster Integrationsfehler V8): Der Salt geht
 * ALS TEXT (ASCII-Bytes des 64-stelligen Hex-Strings) in die Berechnung — er wird
 * NICHT hex-dekodiert. `TextEncoder` liefert genau diese Bytes.
 */

/** Laenge einer HMAC-SHA256-Signatur in Hex-Zeichen. */
export const SIGNATURE_HEX_LENGTH = 64;

/**
 * HMAC-SHA256 als Hex lowercase. Schluessel und Nachricht gehen als UTF-8-Rohtext
 * ein (`TextEncoder`). Fuer den Salt heisst das: die 64 Hex-Zeichen werden als
 * Text-Bytes verwendet, nicht als 32 dekodierte Bytes. Leerer Schluessel ist vom
 * Aufrufer auszuschliessen — WebCrypto lehnt leere HMAC-Keys ohnehin ab.
 */
export async function computeHmacSha256Hex(
  key: string | Uint8Array,
  message: string,
): Promise<string> {
  const encoder = new TextEncoder();
  // String-Schlüssel gehen als UTF-8-Text-Bytes ein (der Salt-als-Text-Kanon);
  // ein `Uint8Array`-Schlüssel geht als ROHE Bytes ein (z. B. der hex-dekodierte
  // Salt im Fingerprint — dessen High-Bytes ≥ 0x80 darf `TextEncoder` NICHT zu
  // 2-Byte-UTF-8 aufblähen, sonst feuert die Diagnose für echte Salts nie).
  // In eine frische, ArrayBuffer-gestützte `Uint8Array` normalisieren (WebCrypto
  // `importKey` erwartet `BufferSource` mit `ArrayBuffer`, nicht `ArrayBufferLike`).
  const keyData = new Uint8Array(
    typeof key === 'string' ? encoder.encode(key) : key,
  );
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyData,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const signature = await crypto.subtle.sign(
    'HMAC',
    cryptoKey,
    encoder.encode(message),
  );
  return Array.from(new Uint8Array(signature))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

export type NormalizedSignature =
  | { ok: true; hex: string }
  | { ok: false; reason: 'empty' | 'notHex' | 'wrongLength' };

/**
 * Kanonische Form fuer den Signatur-Vergleich: saemtliches Whitespace raus (auch
 * intern — Copy-Paste mit Zeilenumbruch), optionales `0x`-/`sha256=`-Praefix
 * abgestreift, lowercase. Ungueltige Eingaben sind KEIN Mismatch, sondern ein
 * Eingabefehler (Spec §Scope 2): falsche Laenge/Nicht-Hex melden `wrongLength`
 * bzw. `notHex`, damit die UI „pruefe deine Eingabe" statt „Signatur falsch" sagt.
 */
export function normalizeSignature(input: string): NormalizedSignature {
  let hex = input.replace(/\s+/g, '');
  hex = hex.replace(/^(0x|sha256=)/i, '');
  hex = hex.toLowerCase();
  if (hex.length === 0) {
    return { ok: false, reason: 'empty' };
  }
  if (!/^[0-9a-f]+$/.test(hex)) {
    return { ok: false, reason: 'notHex' };
  }
  if (hex.length !== SIGNATURE_HEX_LENGTH) {
    return { ok: false, reason: 'wrongLength' };
  }
  return { ok: true, hex };
}
